import i18n from 'i18next';
import { cloneDeep, isEmpty, flatten, find } from 'lodash';
import { middle, popUp } from '../../../../copy/ecap/classesSetupCopy.json';
import {
  coreSubjectsEn,
  getAcademicYearTitles,
} from '../../../../copy/ecap/classesSetupStrings';

export const getSubjectObj = (
  subjectList = Object.values(coreSubjectsEn),
  hsYears = getAcademicYearTitles()
) => {
  const subjectSlotsObj = {};
  const subjectProgressObj = {};

  subjectList.forEach((subject) => {
    subjectProgressObj[subject] = 0;

    return hsYears.forEach((year, index) => {
      const slotKey = `${subject}_${index}`;

      subjectSlotsObj[slotKey] = [];

      return subjectSlotsObj;
    });
  });

  return { subjectSlotsObj, subjectProgressObj };
};

export const getBlankEcapAcadPlan = (ecapPlan) => {
  const ecapPlanCopy = cloneDeep(ecapPlan);

  if (ecapPlanCopy.classes_matched === undefined) {
    const subjectObj = getSubjectObj();

    ecapPlanCopy.classes_matched = subjectObj.subjectSlotsObj;
  }

  return ecapPlanCopy;
};

export const getRequiredClassesByAcadPlan = (acadPlan, classList) => {
  if (classList) {
    return classList.filter((eachClass) => eachClass.acad_plan === acadPlan);
  }

  return [];
};

export const getRequiredClassesBySubject = (subject, acadPlanClasses) => {
  return acadPlanClasses.filter(
    (eachClass) => eachClass.academic_area_id === subject
  );
};

export const getRequiredClassesBySubjectYearSlot = (
  subject,
  yearIndex,
  acadPlan,
  classList
) => {
  const requiredClassesByAcadPlan = classList.filter(
    (eachClass) => eachClass.acad_plan === acadPlan
  );
  const requiredClassesBySubject = requiredClassesByAcadPlan.filter(
    (eachClass) =>
      eachClass.academic_area_id === subject && eachClass.year === yearIndex
  );
  return requiredClassesBySubject;
};

/**
 * @param {array} recommendedClasses List of course name strings
 * @param {object} hsClass
 * @returns String to be used as an aria-label
 */
export const getAriaLabelForClass = (recommendedClasses = [], hsClass) => {
  const [classOne = ''] = recommendedClasses;

  const courseTitle = hsClass.requirement_title || hsClass.titleEn;
  const selectedClassString = i18n.t('Selected Class: {{courseTitle}}.', {
    courseTitle,
  });

  // generate a localized string of class names, e.g.: "class1, class2, or class3" in English
  const disjunctionListFormatter = new Intl.ListFormat(i18n.language, {
    type: 'disjunction',
  });
  const courseNamesList = disjunctionListFormatter.format(recommendedClasses);

  const recommendedClassString =
    recommendedClasses.length > 1
      ? i18n.t('Recommended Class: {{courseNamesList}}', { courseNamesList })
      : i18n.t('Recommended Class: {{courseName}}', { courseName: classOne });

  return `${selectedClassString} ${hsClass.learning_outcomes} ${recommendedClassString}`;
};

export const subjectIdMap = (classSubjectId) => {
  const subjectIds = {
    1: 'English',
    2: 'Math',
    3: 'Laboratory Science',
    4: 'Social Science',
    5: 'Fine Arts / Career & Technical Education',
    6: 'Foreign Language',
    7: 'Electives',
  };

  if (classSubjectId) return subjectIds[classSubjectId];

  return subjectIds;
};

export const getSubjectId = (subject) => {
  const subjectIdMapObj = subjectIdMap();
  const keyMatch = Object.keys(subjectIdMapObj).find(
    (key) => subjectIdMapObj[key] === subject
  );
  const keyNum = parseInt(keyMatch, 10);

  return keyNum;
};

export const isRecommendedClass = (
  hsClass,
  placeholdersIn,
  requiredClassesByAcadPlanBySubject
) => {
  const placeholders = placeholdersIn.map((x) => x?.toLowerCase());
  if (placeholders.includes(hsClass.requirement_title?.toLowerCase()))
    return true;
  // if no titles are provided, return false to avoid showing all classes as recommended
  if (hsClass.requirement_title === undefined) return false;
  const classTitle =
    hsClass.requirement_title.indexOf(' - ') !== -1
      ? hsClass.requirement_title.slice(
          0,
          hsClass.requirement_title.indexOf(' - ')
        )
      : hsClass.requirement_title;
  const meetsOrExceedsRequirements =
    requiredClassesByAcadPlanBySubject.includes(classTitle);
  const isCustom = hsClass.academic_area_id === 'Custom';
  const placeholderContainsInvalidatable = placeholders.some(
    (placeholder) =>
      placeholder.includes('year') ||
      placeholder.includes('elective') ||
      (placeholder.includes('fine art') && placeholder.includes('cte'))
  );

  if (isCustom) return false;

  return (
    meetsOrExceedsRequirements ||
    placeholderContainsInvalidatable ||
    placeholders.length === 0
  );
};

export const idSubjectMap = (classTitle) => {
  const subjectTitleIdMap = {
    English: 1,
    Math: 2,
    'Laboratory Science': 3,
    'Social Science': 4,
    'Fine Arts / Career & Technical Education': 5,
    'Foreign Language': 6,
    Electives: 7,
  };

  return subjectTitleIdMap[classTitle];
};

export const getRequiredClassesByAcadPlanBySubject = (
  subjectId,
  acadPlanClasses,
  acadPlan
) => {
  const arrReqClasses = [];

  acadPlanClasses.forEach((eachClass) => {
    if (
      eachClass.academic_area_id === subjectId &&
      eachClass.acad_plan === acadPlan
    ) {
      arrReqClasses.push(eachClass.titleEn);
    }
  });

  return arrReqClasses;
};

export const acadPlanClassMap = (acadPlans, coreSubjects) => {
  // Create a basic object
  /*
  {
  "ACADPLAN" : {
      AcademicAreaId: {
        coreSubject : Title of Core Subject,
        numReqClasses: 0
      },
      AcademicAreaId: {
        coreSubject : Title of Core Subject,
        numReqClasses: 0
      },
      etc...
    }
  }
  */
  const coreSubjectsInfo = {};

  coreSubjects.forEach((subject, index) => {
    coreSubjectsInfo[index + 1] = {
      coreSubject: subject,
      numReqClasses: 0,
      dropDisabled: false,
      title: Array.from([1, 2, 3, 4], () => []),
    };
  });

  const classMap = {};

  for (let x = 0; x < acadPlans.length; x++) {
    classMap[acadPlans[x]] = coreSubjectsInfo;
  }

  return classMap;
};

export const progressBarsStatus = (
  placedClasses,
  coreSubject,
  reqClassesBySubj
) => {
  let subjectProgress = 0;

  if (coreSubject) {
    const max = reqClassesBySubj[coreSubject].length;

    getAcademicYearTitles().forEach((year, yearIndex) => {
      placedClasses[`${coreSubject}_${yearIndex}`].forEach((placedClass) =>
        placedClass && subjectProgress !== max ? subjectProgress++ : null
      );
    });
  }

  return subjectProgress;
};

export const getDestinationIndex = (
  destinationId,
  placedClasses,
  classIndexToReplace
) => {
  // Class is being added
  if (placedClasses && placedClasses[destinationId].length > 0) {
    // if both spots are filled, return classIndexToReplace
    if (
      !!placedClasses[destinationId][0] &&
      !!placedClasses[destinationId][1] &&
      classIndexToReplace
    ) {
      return classIndexToReplace;
    }
    // if top spots is filled, return 1
    if (placedClasses[destinationId][0]) {
      return 1;
    }
  }
  // as a default, return 0
  return 0;
  // DEV NOTE: This just gets the insertion index position but does not handle making sure the array \
  // has multiple values if the insert position is 0
};

/*
There is a really weird issue where the original object is being updated before execution of the actual increment \
on numReqClasses. This data definitely needs to be double-checked but running this basic SQL command for each acad_plan \
tentatively seems so validate the number of required classes for each core subject within each acad_plan

SELECT * FROM hs_classes_course_map INNER JOIN hs_classes ON hs_classes_course_map.hs_course_id = hs_classes.class_id
WHERE hs_classes_course_map.acad_plan = <ACAD_PLAN_STRING> ORDER BY academic_area_id;
*/
export const getCoreSubjectRequirements = (
  degreeAcadPlans,
  arrayOfRequiredClassesByDegree,
  acadPlan
) => {
  const degreeAcadPlansCopy = cloneDeep(degreeAcadPlans);

  const hsClassesForAcadPlan = arrayOfRequiredClassesByDegree[acadPlan];

  const singleAcadPlanObj = cloneDeep(degreeAcadPlansCopy[acadPlan]);

  Object.values(hsClassesForAcadPlan).forEach((value) => {
    singleAcadPlanObj[value.academic_area_id].numReqClasses += 1;
    singleAcadPlanObj[value.academic_area_id].title[value.year - 1].splice(
      value.year - 1,
      0,
      value.titleEn
    );
  });
  degreeAcadPlansCopy[acadPlan] = singleAcadPlanObj;

  return degreeAcadPlansCopy;
};

export const getFilteredListOfClasses = (classList, placedClasses) => {
  const placedClassesFlattened = flatten(Object.values(placedClasses));
  const filteredClasses = Object.values(classList).filter((eachClass) => {
    return !find(placedClassesFlattened, eachClass);
  });

  return filteredClasses;
};

export const reorder = (list, startIndex, endIndex) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);

  result.splice(endIndex, 0, removed);

  return result;
};

const grid = 8;

export const getCustomItemStyle = (draggableStyle, borderColor) => ({
  // some basic styles to make the items look a bit nicer
  userSelect: 'none',
  padding: grid * 2,
  border: `1px solid ${borderColor}`,
  borderRadius: '9px',
  width: '95%',
  color: 'black',
  display: 'flex',
  background: 'white',
  overflow: 'visible',
  alignItems: 'center',
  height: '55px',

  // styles we need to apply on draggables
  ...draggableStyle,
});

export const getYearListItemStyle = (
  borderColor,
  provided,
  droppedCount,
  droppableBeingDraggedOver
) => {
  return {
    userSelect: 'none',
    border: `1px solid ${borderColor}`,
    borderRadius: '9px',
    width: '79%',
    color: 'black',
    display: 'flex',
    justifyContent: 'flex-start',
    background: 'white',
    overflow: 'visible',
    height:
      droppedCount > 1 || (droppedCount === 1 && droppableBeingDraggedOver)
        ? '35px'
        : '55px',
    margin:
      droppedCount > 1 || (droppedCount === 1 && droppableBeingDraggedOver)
        ? '3.5px 0 3px'
        : '0',
    alignItems: 'center',
    textAlign: 'left',
  };
};

export const getClassListItemStyle = (
  draggableStyle,
  borderColor,
  mobile = false
) => ({
  // some basic styles to make the items look a bit nicer
  userSelect: 'none',
  padding: grid * 2,
  margin: `0 0 ${grid}px 0`,
  border: `1px solid ${borderColor}`,
  borderRadius: '9px',
  color: mobile ? 'black' : 'grey',
  overflow: 'visible',
  alignItems: 'center',
  height: '55px',
  display: 'flex',

  // styles we need to apply on draggables
  ...draggableStyle,
});

export const getYearListStyleGrey = () => {
  return {
    color: 'black',
    background: '#F3F1F1',
    overflowY: 'visible',
    transform: 'none !important',
  };
};

export const getYearListStyleWhite = () => ({
  color: 'black',
  overflowY: 'visible',
  transform: 'none !important',
  background: 'white',
});

export const getClassListStyle = (mobile = false) => ({
  padding: '8px 0',
  width: '95%',
  overflowY: 'visible',
  marginLeft: mobile ? '7%' : '0',
});

export const getCustomDraggableId = (
  classTitle,
  subjectYearIndex,
  customClassDraggable
) => {
  if (!customClassDraggable) return `${classTitle}_${subjectYearIndex}_custom`;

  return `${classTitle}_${subjectYearIndex}`;
};

export const checkIsDropDisabled = (
  customDrag,
  subjectYearSlots,
  currentDraggableSubject,
  subject,
  index,
  customClassDraggable
) => {
  // Dragging custom class between year columns
  if (customDrag && subjectYearSlots[`${subject}_${index}`].length < 2)
    return true;
  if (
    subject === currentDraggableSubject &&
    subjectYearSlots[`${subject}_${index}`].length < 2
  )
    return true;
  if (!isEmpty(customClassDraggable)) return true;
};
